
import React, { useEffect, useMemo } from 'react';
import { Opportunity, OpportunitySEStatus, OpportunitySETag, OpportunityUpdate } from '../../types/opportunity';
import { HbrCheckbox, HbrCheckboxGroup, HbrIcon, HbrOption, HbrSelect, HbrSpinner, HbrTextarea } from '@harbor/elements-react-standalone';
import { alertsPositiveSmallBorder } from '@harbor/elements/icons';
import { getOpportunityStatusColor } from '../../support/opportunityHelpers';
import { asSingleValue } from '../../support/utils';


interface OpportunityStatusModalProps {
  opportunities: Opportunity[];
  updates: Record<string, (OpportunitySEStatus|OpportunitySETag|string)>;
  setOpportunityUpdate: ( update: OpportunityUpdate, value: OpportunitySEStatus | OpportunitySETag | string, resetUpdates?: boolean) => void;
  loading: Record<string, boolean>;
  success: Record<string, boolean>;
  multi: boolean;
}

const OpportunityStatusModal: React.FC<OpportunityStatusModalProps> = ({
  opportunities,
  updates,
  setOpportunityUpdate,
  loading,
  success,
  multi
}) => {
  const initialized = React.useRef(false);

  const getInputValueForStatus = (status: OpportunitySEStatus) => {
    switch (status) {
      case OpportunitySEStatus.NoneAssigned:
      case OpportunitySEStatus.NotRequired:
      case OpportunitySEStatus.TechnicalOnHold:
        return OpportunitySEStatus.WorkInProgress;
      case OpportunitySEStatus.WorkInProgress:
        return OpportunitySEStatus.TechnicalOnHold;
      default:
        return undefined;
    }
  }

  const options = useMemo(() => {
    if (opportunities.length !== 1) return [
      OpportunitySEStatus.NotRequired,
      OpportunitySEStatus.TechnicalOnHold,
      OpportunitySEStatus.WorkInProgress
    ];

    switch (opportunities[0].SE_Status__c as OpportunitySEStatus) {
      case OpportunitySEStatus.NoneAssigned:
      case OpportunitySEStatus.Unknown:
        return [
          OpportunitySEStatus.NotRequired,
          OpportunitySEStatus.TechnicalOnHold,
          OpportunitySEStatus.WorkInProgress
        ];
      case OpportunitySEStatus.TechnicalOnHold:
        return [
          OpportunitySEStatus.NotRequired,
          OpportunitySEStatus.WorkInProgress
        ];
      case OpportunitySEStatus.WorkInProgress:
        return [
          OpportunitySEStatus.NotRequired,
          OpportunitySEStatus.TechnicalOnHold
        ];
      case OpportunitySEStatus.NotRequired:
        return [
          OpportunitySEStatus.WorkInProgress,
          OpportunitySEStatus.TechnicalOnHold
        ];
      default:
        return [];
    }
  }, [opportunities]);

  const isTaggedUpdate = (updates) => {
    return opportunities[0].isTagged
      ? updates[OpportunityUpdate.Tag] === OpportunitySETag.UntagSE
      : updates[OpportunityUpdate.Tag] === OpportunitySETag.TagSE
  };

  const presetStatusUpdate = useMemo(() => {
    if (!multi && isTaggedUpdate(updates)) { return undefined }
    if (!multi && !updates[OpportunityUpdate.Status]) {
      return getInputValueForStatus(opportunities[0].SE_Status__c as OpportunitySEStatus);
    }
    return updates[OpportunityUpdate.Status];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunities, updates]);

  useEffect(() => {
    if (presetStatusUpdate && !initialized.current) {
      const selected = getInputValueForStatus(opportunities[0].SE_Status__c as OpportunitySEStatus);
      setOpportunityUpdate(OpportunityUpdate.Status, selected);
      initialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetStatusUpdate, initialized.current]);

  const handleTagCheckChange = (value: string) => {
    const resetUpdates = !multi && 
      (opportunities[0].isTagged
        ? value === OpportunitySETag.UntagSE
        : value === OpportunitySETag.TagSE
      );
    setOpportunityUpdate(OpportunityUpdate.Tag, value, resetUpdates);
  };

  return (<>
    {/** Selected Opportunities List */}
    <div className='border border-gray-300 rounded-md p-2'>
      {opportunities.map((opportunity) => {
        const statusColor = getOpportunityStatusColor((opportunity.SE_Status__c as OpportunitySEStatus), opportunity.isTagged);
        return (
          <div className='grid grid-cols-[160px_1fr_24px] py-1 gap-4 items-center' key={opportunity.id}>
            <div className='flex row items-center border border-gray-400 border-1 p-1 rounded-md'>
              <div className={`rounded-full ${statusColor} w-3 h-3 mr-2 ml-1`}></div>
              <div className='text-xs'>{opportunity.SE_Status__c ?? OpportunitySEStatus.Unknown}</div>
            </div>
            <div className='text-sm truncate'>{opportunity.Name}</div>
            <div className='mr-2 flex items-center'>
              {!loading[opportunity.Id] && !success[opportunity.Id] && (
                <div className={`rounded-full border border-gray-400 w-4 h-4`}></div>
              )}
              {loading[opportunity.Id] && !success[opportunity.Id] && (
                <HbrSpinner size='small' />
              )}
              {!loading[opportunity.Id] && success[opportunity.Id] && (
                <HbrIcon asset={alertsPositiveSmallBorder} sentiment="positive"></HbrIcon>
              )}
            </div>
          </div>
        )
      })}
    </div>

    {/** Tag / Untag */}
    <div className='py-2 flex flex-col w-full'>
      {!multi
        ? (opportunities[0].SE_Status__c as OpportunitySEStatus === OpportunitySEStatus.WorkInProgress) && (
          <div className='my-4 grid grid-cols-[160px_1fr] gap-2 items-center'>
            <div className='text-sm font-bold text-gray-700'>{opportunities[0].isTagged ? 'Untag Yourself' : 'Tag Yourself'}</div>
            <HbrCheckbox
              checked={
                (opportunities[0].isTagged && updates[OpportunityUpdate.Tag] === OpportunitySETag.UntagSE) ||
                (!opportunities[0].isTagged && updates[OpportunityUpdate.Tag] === OpportunitySETag.TagSE)
              }
              onHbr-click={(e) => {
                if (opportunities[0].isTagged) {
                  handleTagCheckChange(updates[OpportunityUpdate.Tag] === OpportunitySETag.UntagSE ? undefined : OpportunitySETag.UntagSE);
                } else {
                  handleTagCheckChange(updates[OpportunityUpdate.Tag] === OpportunitySETag.TagSE ? undefined : OpportunitySETag.TagSE);
                }
              }}
            />
          </div>)
        : (<div className='my-4 grid grid-cols-[160px_1fr] gap-2 items-center'>
            <div className='text-sm font-bold text-gray-700'>SE Tagged</div>
            <HbrCheckboxGroup direction='row'>
              <HbrCheckbox
                checked={updates[OpportunityUpdate.Tag] === OpportunitySETag.TagSE}
                onHbr-click={(e) => handleTagCheckChange(updates[OpportunityUpdate.Tag] === OpportunitySETag.TagSE ? undefined : OpportunitySETag.TagSE)}
                className='mr-4'
              >Tag Yourself</HbrCheckbox>
              <HbrCheckbox
                checked={updates[OpportunityUpdate.Tag] === OpportunitySETag.UntagSE}
                onHbr-click={(e) => handleTagCheckChange(updates[OpportunityUpdate.Tag] === OpportunitySETag.UntagSE ? undefined : OpportunitySETag.UntagSE)}
              >Untag Yourself</HbrCheckbox>
            </HbrCheckboxGroup>
          </div>)
      }

      {/** SE Status */}
      <HbrSelect
        placeholder='Select status action'
        label='SE Status'
        onHbr-change={(e) => setOpportunityUpdate(OpportunityUpdate.Status, asSingleValue(e.target.value))}
        value={(updates[OpportunityUpdate.Status] || presetStatusUpdate || '') as string}
        disabled={Object.keys(updates).includes(OpportunityUpdate.Tag) && [OpportunitySETag.UntagSE, OpportunitySETag.TagSE].includes(updates[OpportunityUpdate.Tag] as OpportunitySETag)}
        className='w-full'
      >
        {options.sort((a, b) => a.localeCompare(b)).map((option) => (
          <HbrOption key={`${opportunities[0].id}-${option}`} value={option as string}>
            {option}
          </HbrOption>
        ))}
      </HbrSelect>

      {/** SE Note */}
      {multi && (
        <HbrTextarea
          label='SE Note'
          className='w-full mt-4'
          placeholder='Add an SE note'
          value={updates[OpportunityUpdate.Note] || ''}
          onHbr-change={(e) => setOpportunityUpdate(OpportunityUpdate.Note, e.target.value)}
        ></HbrTextarea>
      )}
    </div>
  </>);
};
export default OpportunityStatusModal;